import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { ArrowLeft, ShieldCheck, Gavel, Check, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

function fmtUSD(n) {
  if (!n) return "$0";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n}`;
}

export default function MarketplaceDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  const [bidOpen, setBidOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/marketplace/listings/${id}`);
      setListing(data);
      setAmount(String(data.min_bid_usd));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const placeBid = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/marketplace/bids", {
        listing_id: id,
        amount_usd: parseInt(amount, 10),
        message,
      });
      toast.success("Bid placed — funds held in mock escrow");
      setBidOpen(false);
      setMessage("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const acceptBid = async (bid) => {
    if (!window.confirm(`Accept bid of ${fmtUSD(bid.amount_usd)} from ${bid.bidder_name}? This closes the listing.`)) return;
    try {
      await api.post(`/marketplace/bids/${bid.id}/accept`);
      toast.success("Bid accepted — deal closed, escrow released");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const rejectBid = async (bid) => {
    if (!window.confirm("Reject this bid?")) return;
    try {
      await api.post(`/marketplace/bids/${bid.id}/reject`);
      toast.success("Bid rejected — escrow refunded");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const withdrawListing = async () => {
    if (!window.confirm("Withdraw this listing? All pending bids will be refunded.")) return;
    try {
      await api.post(`/marketplace/listings/${id}/close`);
      toast.success("Listing withdrawn");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!listing) return <div className="p-8 eyebrow text-slate-500">Loading listing…</div>;
  const isOwner = user && listing.owner_id === user.id;
  const isClosed = listing.status !== "open";

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto fade-in-up" data-testid="listing-detail-page">
      <button onClick={() => nav(-1)} className="text-sm text-slate-600 hover:text-[#B33A3A] flex items-center gap-1.5 mb-4" data-testid="back-btn">
        <ArrowLeft size={14} /> Back to marketplace
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bengal-card p-6 md:p-8">
            <div className="flex items-center gap-2 mb-3">
              <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${
                listing.listing_type === "sale"
                  ? "bg-red-50 text-[#7A1F2B] border-red-200"
                  : "bg-amber-50 text-amber-800 border-amber-200"
              }`}>
                {listing.listing_type}
              </span>
              <span className="eyebrow text-slate-500 capitalize">{listing.asset_type}</span>
              <span className="eyebrow text-slate-300">·</span>
              <span className="eyebrow font-mono text-slate-500">{listing.asset_country}</span>
              <span className="eyebrow text-slate-300">·</span>
              <span className="eyebrow text-slate-500">{listing.asset_category}</span>
            </div>
            <h1 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight leading-tight mb-2">{listing.asset_title}</h1>
            <div className="text-sm text-slate-500">Listed by <span className="text-slate-800 font-medium">{listing.owner_name}</span></div>

            <div className="mt-6 prose prose-sm text-slate-700">
              <p>{listing.description}</p>
            </div>

            {listing.asset && (
              <div className="mt-6 pt-6 border-t border-slate-100">
                <div className="eyebrow mb-3">Asset Details</div>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  <dt className="text-slate-500">Filing Number</dt><dd className="font-mono">{listing.asset.filing_number || "—"}</dd>
                  <dt className="text-slate-500">Filing Date</dt><dd>{listing.asset.filing_date || "—"}</dd>
                  <dt className="text-slate-500">Status</dt><dd className="capitalize">{listing.asset.status}</dd>
                  <dt className="text-slate-500">Renewal Due</dt><dd>{listing.asset.renewal_due || "—"}</dd>
                </dl>
                {listing.asset.abstract && (
                  <>
                    <div className="eyebrow mt-5 mb-2">Abstract</div>
                    <p className="text-sm text-slate-700 leading-relaxed">{listing.asset.abstract}</p>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Bids */}
          <div className="bengal-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="eyebrow">Active Bids ({listing.bids?.length || 0})</div>
              {isOwner && !isClosed && (
                <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-[#FAD4D4] text-[#7A1F2B] font-semibold">Your listing</span>
              )}
            </div>
            {(!listing.bids || listing.bids.length === 0) && <div className="text-sm text-slate-500">No bids yet. Be the first.</div>}
            <div className="space-y-3">
              {(listing.bids || []).map((b) => (
                <div key={b.id} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0" data-testid={`bid-${b.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium flex items-center gap-2">
                      {b.bidder_name}
                      <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${
                        b.status === "accepted" ? "bg-green-50 text-green-800 border-green-200" :
                        b.status === "rejected" || b.status === "withdrawn" ? "bg-slate-50 text-slate-500 border-slate-200" :
                        "bg-amber-50 text-amber-800 border-amber-200"
                      }`}>
                        {b.status}
                      </span>
                    </div>
                    {b.message && <div className="text-xs text-slate-500 mt-0.5">"{b.message}"</div>}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className="font-mono text-[#B33A3A] font-semibold">{fmtUSD(b.amount_usd)}</div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1 justify-end mt-0.5">
                        <ShieldCheck size={10} /> {b.escrow_status?.replace("_mock", "") || "Escrow held"}
                      </div>
                    </div>
                    {isOwner && !isClosed && b.status === "pending" && (
                      <div className="flex items-center gap-1">
                        <button onClick={() => acceptBid(b)} data-testid={`accept-bid-${b.id}`} title="Accept" className="p-1.5 bg-green-50 text-green-700 hover:bg-green-100 rounded-sm border border-green-200">
                          <Check size={14} />
                        </button>
                        <button onClick={() => rejectBid(b)} data-testid={`reject-bid-${b.id}`} title="Reject" className="p-1.5 bg-slate-50 text-slate-600 hover:bg-red-50 hover:text-red-600 rounded-sm border border-slate-200">
                          <X size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right — price sidebar */}
        <div className="space-y-4 lg:sticky lg:top-6 self-start">
          <div className="bengal-card p-6">
            <div className="flex items-center justify-between mb-1">
              <div className="eyebrow">{isClosed ? "Sold for" : "Asking Price"}</div>
              {isClosed && (
                <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-slate-900 text-white font-semibold">
                  {listing.status}
                </span>
              )}
            </div>
            <div className="font-mono text-4xl text-[#B33A3A] font-semibold tracking-tight mt-1">
              {fmtUSD(isClosed && listing.sold_price_usd ? listing.sold_price_usd : listing.asking_price_usd)}
            </div>
            {!isClosed && <div className="text-xs text-slate-500 mt-1">Min bid: {fmtUSD(listing.min_bid_usd)}</div>}

            {isOwner ? (
              !isClosed ? (
                <button
                  onClick={withdrawListing}
                  data-testid="withdraw-listing-btn"
                  className="w-full mt-5 text-sm border border-slate-300 hover:border-red-600 hover:text-red-600 rounded-sm py-2.5 transition-colors"
                >
                  Withdraw listing
                </button>
              ) : (
                <div className="mt-5 text-xs text-slate-500 text-center py-3 border border-slate-200 bg-slate-50 rounded-sm">
                  Listing is {listing.status}.
                </div>
              )
            ) : !isClosed ? (
              <button
                onClick={() => setBidOpen(true)}
                data-testid="open-bid-btn"
                className="w-full bengal-btn-primary text-sm mt-5 flex items-center justify-center gap-2"
              >
                <Gavel size={15} /> Place Bid / Offer
              </button>
            ) : (
              <div className="mt-5 text-xs text-slate-500 text-center py-3 border border-slate-200 bg-slate-50 rounded-sm">
                This listing is closed.
              </div>
            )}

            <div className="mt-4 p-3 bg-slate-50 rounded-sm border border-slate-200">
              <div className="flex items-center gap-2 text-xs font-medium text-slate-700 mb-1">
                <ShieldCheck size={13} className="text-[#B33A3A]" /> Mock Escrow Protection
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">
                Bids are held in a demo escrow account until the seller accepts. Funds release on asset transfer.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={bidOpen} onOpenChange={setBidOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Place your bid</DialogTitle>
          </DialogHeader>
          <form onSubmit={placeBid} className="space-y-4 pt-2" data-testid="bid-form">
            <div>
              <label className="eyebrow block mb-1.5">Amount (USD)</label>
              <input
                type="number"
                required
                min={listing.min_bid_usd}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                data-testid="bid-amount-input"
                className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#B33A3A]"
              />
              <div className="text-xs text-slate-500 mt-1">Minimum: {fmtUSD(listing.min_bid_usd)}</div>
            </div>
            <div>
              <label className="eyebrow block mb-1.5">Message (optional)</label>
              <textarea
                rows={3}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                data-testid="bid-message-input"
                className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm"
              />
            </div>
            <button type="submit" disabled={submitting} data-testid="submit-bid-btn" className="bengal-btn-primary w-full text-sm disabled:opacity-50">
              {submitting ? "Placing bid…" : `Bid ${fmtUSD(parseInt(amount || 0, 10))}`}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
